import { isApiFullLandingPageLink } from '@storefronts/common-types'

import { CartItem } from '../types'

export type CartUpsellCandidate = {
    linkHash: string
    cartUpsellRank: number
}

/**
 * Collects co-purchase upsell candidates for the current cart. For each in-cart variant we read the
 * upsells the backend attached to its anchor link (keyed by that variant's `shopifyId`), drop any
 * recommendation whose variant is already in the cart, dedupe across cart items keeping the
 * strongest (lowest) rank, and order by rank ascending (link hash as a stable tiebreak). Returns
 * ordered link hashes for the caller to resolve into landing page links. Derived purely from
 * `cartItems`, so the result changes whenever a variant is added or removed.
 */
export function collectCartUpsellCandidates(
    cartItems: CartItem[],
): CartUpsellCandidate[] {
    const inCartVariantIds = new Set<string>()
    for (const item of cartItems) {
        if (item.variant.shopifyId) inCartVariantIds.add(item.variant.shopifyId)
    }

    const bestRankByHash = new Map<string, number>()
    for (const item of cartItems) {
        const anchorVariantId = item.variant.shopifyId
        const link = item.variant.link
        const upsells =
            link && isApiFullLandingPageLink(link)
                ? link.cartUpsellVariants?.[anchorVariantId]
                : undefined
        if (!upsells) continue

        for (const upsell of upsells) {
            if (inCartVariantIds.has(upsell.cartUpsellVariantId)) continue
            const existing = bestRankByHash.get(upsell.linkHash)
            if (existing == null || upsell.cartUpsellRank < existing) {
                bestRankByHash.set(upsell.linkHash, upsell.cartUpsellRank)
            }
        }
    }

    return [...bestRankByHash.entries()]
        .map(([linkHash, cartUpsellRank]) => ({ linkHash, cartUpsellRank }))
        .sort((a, b) =>
            a.cartUpsellRank !== b.cartUpsellRank
                ? a.cartUpsellRank - b.cartUpsellRank
                : a.linkHash.localeCompare(b.linkHash),
        )
}
